import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getProduct, getProducts } from "../utils/api";
import { useCart } from "../context/CartContext";
import { useToast } from "../components/ToastProvider";
import "./ProductPage.css";
import SEO from "../components/SEO";
import { Helmet } from "react-helmet-async";

export default function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedTexture, setSelectedTexture] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const { addItem, cart } = useCart();
  const { showToast } = useToast();

  useEffect(() => {
    getProduct(id)
      .then((res) => {
        const data = res.data.data;
        setProduct(data);
        // Встановлюємо перший розмір/текстуру за замовчуванням
        if (data.sizes && data.sizes.length > 0) setSelectedSize(data.sizes[0]);
        if (data.textures && data.textures.length > 0) setSelectedTexture(data.textures[0]);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (product?.categoryId) {
      getProducts({ categoryId: product.categoryId, limit: 4 })
        .then((res) => {
          const all = Array.isArray(res.data?.data) ? res.data.data : res.data;

          setSimilarProducts(all.filter((p) => p.id !== product.id).slice(0, 4));
        })
        .catch(console.error);
    }
  }, [product]);

  // Підрахунок фінальної ціни
  const basePrice = product ? Number(product.basePrice) : 0;
  const sizeExtra = selectedSize?.price ? Number(selectedSize.price) : 0;
  const textureExtra = selectedTexture?.price ? Number(selectedTexture.price) : 0;
  const unitPrice = basePrice + sizeExtra + textureExtra;
  const finalPrice = unitPrice * qty;

  const inCart = cart.find((i) => i.id === product?.id);

  const handleAdd = () => {
    const item = {
      ...product,
      selectedSize,
      selectedTexture,
      unitPrice,
    };
    for (let i = 0; i < qty; i++) addItem(item);
    showToast(`«${product.name}» додано до кошика (${qty} шт.)`, "success");
  };

  const getImageUrl = (img) => {
    if (!img) return null;
    return img.startsWith("http") ? img : `${img}`;
  };

  // Збираємо масив зображень (головне + додаткові)
  const images = product ? [product.image, ...(product.images || [])].filter(Boolean) : [];

  if (loading) return <div className="spinner" style={{ minHeight: "60vh" }} />;
  if (!product)
    return (
      <div className="not-found">
        <SEO
          title="Товар не знайдено"
          description="Товар не знайдено. Повернутися до каталогу."
          url="https://zori.place/product/not-found"
        />
        <h2>Товар не знайдено</h2>
        <Link to="/catalog" className="btn-primary">
          ← Повернутися до каталогу
        </Link>
      </div>
    );

  return (
    <div className="product-page page-enter">
      {product && (
        <SEO
          title={product.name}
          description={product.description?.slice(0, 160) || "Унікальний декор для вашої кімнати"}
          image={product.images?.[0] ? `https://zori.place${product.images[0]}` : undefined}
          url={`https://zori.place/product/${product.id}`}
        />
      )}
      {product && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              name: product.name,
              description: product.description,
              image: product.images?.[0] ? `https://zori.place${product.images[0]}` : undefined,
              offers: {
                "@type": "Offer",
                price: product.basePrice,
                priceCurrency: "UAH",
                availability: "https://schema.org/InStock",
                seller: { "@type": "Organization", name: "Zori.place" },
              },
            })}
          </script>
        </Helmet>
      )}
      <div className="container">
        {/* Хлібні крихти */}
        <div className="product-breadcrumb">
          <Link to="/">Головна</Link>
          <span>›</span>
          <Link to="/catalog">Каталог</Link>
          {product.category && (
            <>
              <span>›</span>
              <Link to={`/catalog?category=${product.category}`}>{product.category}</Link>
            </>
          )}
          <span>›</span>
          <span>{product.name}</span>
        </div>

        {/* Основний блок */}
        <div className="product-detail">
          {/* Галерея зображень */}
          <div className="product-gallery">
            <div className="product-gallery-main">
              {images.length > 0 ? (
                   <img
      src={getImageUrl(images[selectedImage])}
      alt={product.name}
      className="gallery-main-img"
      onClick={() => setLightboxOpen(true)}
    />
              ) : (
                <div className="product-img-placeholder">✿</div>
              )}
              {product.category && <span className="product-category-tag">{product.category}</span>}
            </div>
            {images.length > 1 && (
              <div className="product-gallery-thumbs">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    className={`thumb-btn${selectedImage === idx ? " active" : ""}`}
                    onClick={() => setSelectedImage(idx)}
                  >
                    <img src={getImageUrl(img)} alt={`${product.name} ${idx + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Інформація */}
          <div className="product-detail-info">
            {product.category && <p className="product-detail-cat">{product.category}</p>}
            <h1 className="product-detail-name">{product.name}</h1>

            <div className="product-detail-price">{finalPrice.toFixed(0)} ₴</div>

            {/* Вибір розміру */}
            {product.sizes && product.sizes.length > 0 && (
              <div className="product-option-group">
                <p className="option-label">Розмір</p>
                <div className="option-buttons">
                  {product.sizes.map((size) => (
                    <button
                      key={size.id}
                      className={`option-btn${selectedSize?.name === size.name ? " active" : ""}`}
                      onClick={() => setSelectedSize(size)}
                    >
                      {size.name || size.value}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Опис розмірів */}
            {product.sizes && product.sizes.length > 0 && (
              <div className="product-option-group-description">
                {product.sizes.map((size) => (
                  <div key={size.id}>
                    <p>
                        {size.name} = {(basePrice + Number(size.price || 0)).toFixed(0)} грн
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Вибір текстури */}
            {product.textures && product.textures.length > 0 && (
              <div className="product-option-group">
                <p className="option-label">Текстура</p>
                <div className="option-buttons">
                  {product.textures?.map((texture) => (
                    <button
                      key={texture.id}
                      className={`texture-btn${selectedTexture?.name === texture.name ? " active" : ""}`}
                      onClick={() => setSelectedTexture(texture)}
                    >
                      <img
                        src={getImageUrl(texture.image)}
                        alt={texture.name}
                        className="texture-img"
                      />
                      <span>{texture.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Опис текстур */}
            {product.textures && product.textures.length > 0 && (
              <div className="product-option-group-description">
                {product.textures.map((texture) => (
                  <div key={texture.id}>
                    <p>
  {texture.name} = {(basePrice + Number(texture.price || 0)).toFixed(0)} грн
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Кількість + Кнопка */}
            <div className="product-detail-actions">
              <div className="qty-control">
                <button onClick={() => setQty(Math.max(1, qty - 1))}>−</button>
                <span>{qty}</span>
                <button onClick={() => setQty(qty + 1)}>+</button>
              </div>
              <button className="btn-primary add-to-cart-btn" onClick={handleAdd}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="cart-icon"
                >
                  <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {inCart ? "✓ Ще до кошика" : "Додати до кошика"}
              </button>
            </div>

            {inCart && (
              <div className="product-in-cart-notice">
                ✓ Товар вже у кошику ({inCart.quantity} шт.) —{" "}
                <Link to="/cart">Перейти до кошика</Link>
              </div>
            )}

            {/* Мета-інформація */}
            <div className="product-detail-meta">
              <div className="meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
                <span>Доставка по всій Україні</span>
              </div>
              <div className="meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <span>Гарантія якості</span>
              </div>
              <div className="meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span>Зручне повернення</span>
              </div>
            </div>
          </div>
        </div>

        {/* Опис товару */}
        {product.description && (
          <div className="product-description-section">
            <h2 className="section-title">Опис товару</h2>
            <div className="product-description-text">
              {product.description
                .split("\n")
                .map((line, i) => (line.trim() ? <p key={i}>{line}</p> : null))}
            </div>
          </div>
        )}

        {/* Схожі товари */}
        {similarProducts.length > 0 && (
          <div className="similar-products-section">
            <h2 className="section-title similar-title">Схожі варіанти</h2>
            <div className="similar-products-grid">
              {similarProducts.map((item) => {
                const imgUrl = item.images[0]
                  ? item.images[0].startsWith("http")
                    ? item.images[0]
                    : `${item.images[0]}`
                  : null;
                return (
                  <Link to={`/product/${item.id}`} key={item.id} className="similar-card">
                    <div className="similar-card-image">
                      {imgUrl ? (
                        <img src={item.images[0]} alt={item.name} />
                      ) : (
                        <div className="similar-img-placeholder">✿</div>
                      )}
                    </div>
                    <div className="similar-card-info">
                      <p className="similar-card-name">{item.name}</p>
                      <p className="similar-card-price">{Number(item.basePrice).toFixed(0)} ₴</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
	{lightboxOpen && (   // <-- сюда, после .product-page но внутри return
      <div className="lightbox-overlay" onClick={() => setLightboxOpen(false)}>
        <button className="lightbox-close" onClick={() => setLightboxOpen(false)}>✕</button>
        <img
          src={getImageUrl(images[selectedImage])}
          alt={product.name}
          className="lightbox-img"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )}
    </div>
  );
}
